/**
 * SieFilPage - Guide about SIE files
 *
 * SEO Target: /arsredovisning/guider/sie-fil
 * Primary keywords: SIE-fil, exportera SIE, SIE4-fil, SIE-format
 */

import { Link } from "react-router-dom";
import { PageMeta, getBreadcrumbSchema, SEO_CONFIG } from "@/lib/seo";
import { Breadcrumbs } from "@/layouts/PublicLayout";
import { Helmet } from "react-helmet-async";

const CheckIcon = () => (
  <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

export function SieFilPage() {
  const breadcrumbItems = [
    { label: "Årsredovisning", href: "/arsredovisning" },
    { label: "Guider", href: "/arsredovisning/guider" },
    { label: "SIE-fil" },
  ];

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: "Hem", url: "/" },
    { name: "Årsredovisning", url: "/arsredovisning" },
    { name: "Guider", url: "/arsredovisning/guider" },
    { name: "SIE-fil", url: "/arsredovisning/guider/sie-fil" },
  ]);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Vad är en SIE-fil? Komplett guide till SIE-formatet",
    description:
      "Lär dig allt om SIE-filer - Sveriges standardformat för bokföringsdata. Hur du exporterar, vilka typer som finns och hur de används.",
    author: {
      "@type": "Organization",
      name: "Kvanten",
    },
    publisher: {
      "@type": "Organization",
      name: "Kvanten",
      url: SEO_CONFIG.siteUrl,
    },
    datePublished: "2025-01-15",
    dateModified: "2025-01-30",
  };

  return (
    <>
      <PageMeta
        title="Vad är en SIE-fil? | Guide till SIE-formatet | Kvanten"
        description="Komplett guide om SIE-filer - Sveriges standardformat för bokföringsdata. Lär dig exportera SIE4-filer från Fortnox, Visma och andra program."
        keywords="SIE-fil, SIE4, SIE-format, exportera SIE, bokföringsdata, SIE-standard"
        canonicalUrl={`${SEO_CONFIG.siteUrl}/arsredovisning/guider/sie-fil`}
        fullTitle
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-b from-purple-50 to-white py-12 lg:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={breadcrumbItems} />

          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <span>Guide</span>
            <span>•</span>
            <span>8 min läsning</span>
            <span>•</span>
            <span>Uppdaterad jan 2025</span>
          </div>

          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            Vad är en SIE-fil? Komplett guide till SIE-formatet
          </h1>

          <p className="text-xl text-gray-600">
            SIE är Sveriges standardformat för att flytta bokföringsdata mellan olika program. Lär
            dig allt du behöver veta om SIE-filer och hur du använder dem för årsredovisning.
          </p>
        </div>
      </section>

      {/* Content */}
      <article className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Table of contents */}
          <div className="bg-gray-50 rounded-xl p-6 mb-12">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Innehåll</h2>
            <nav>
              <ol className="space-y-2 text-blue-600">
                <li>
                  <a href="#vad-ar-sie" className="hover:underline">
                    1. Vad är SIE?
                  </a>
                </li>
                <li>
                  <a href="#sie-typer" className="hover:underline">
                    2. Olika SIE-typer
                  </a>
                </li>
                <li>
                  <a href="#innehall" className="hover:underline">
                    3. Vad innehåller en SIE-fil?
                  </a>
                </li>
                <li>
                  <a href="#exportera" className="hover:underline">
                    4. Så exporterar du SIE
                  </a>
                </li>
                <li>
                  <a href="#vanliga-fragor" className="hover:underline">
                    5. Vanliga frågor
                  </a>
                </li>
              </ol>
            </nav>
          </div>

          {/* Section 1 */}
          <section id="vad-ar-sie" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Vad är SIE?</h2>
            <p className="text-gray-600 mb-4">
              SIE (Standard Import Export) är ett svenskt standardformat för utbyte av
              bokföringsdata mellan olika program. Formatet utvecklades på 1980-talet av en
              arbetsgrupp bestående av representanter från FAR, BAS-intressenternas Förening och
              ledande programvaruföretag.
            </p>
            <p className="text-gray-600 mb-4">
              Idag förvaltas standarden av SIE-gruppen (sie.se) och används av i princip alla
              bokföringsprogram i Sverige. Detta gör det möjligt att:
            </p>
            <ul className="space-y-2 mb-4">
              {[
                "Flytta bokföring mellan olika program",
                "Skicka bokföringsdata till revisor eller redovisningskonsult",
                "Importera data till system för årsredovisning eller analys",
                "Arkivera bokföring i ett öppet format",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckIcon />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>

            <div className="bg-blue-50 rounded-lg p-6 mt-6">
              <h3 className="font-semibold text-gray-900 mb-2">SIE är en öppen standard</h3>
              <p className="text-gray-600">
                Till skillnad från proprietära format är SIE en öppen standard. Det betyder att alla
                programvaruföretag kan implementera stöd för SIE utan licensavgifter, vilket
                garanterar att du inte blir inlåst hos en leverantör.
              </p>
            </div>
          </section>

          {/* Section 2 */}
          <section id="sie-typer" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Olika SIE-typer</h2>
            <p className="text-gray-600 mb-6">
              Det finns flera versioner av SIE-formatet för olika ändamål:
            </p>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left py-3 px-4 border-b font-semibold">Typ</th>
                    <th className="text-left py-3 px-4 border-b font-semibold">Innehåll</th>
                    <th className="text-left py-3 px-4 border-b font-semibold">Användning</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="py-3 px-4 border-b font-medium">SIE 1</td>
                    <td className="py-3 px-4 border-b">Årssaldon</td>
                    <td className="py-3 px-4 border-b">Enkel export av kontosaldon</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 border-b font-medium">SIE 2</td>
                    <td className="py-3 px-4 border-b">Periodsaldon</td>
                    <td className="py-3 px-4 border-b">Månadsvisa saldon</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 border-b font-medium">SIE 3</td>
                    <td className="py-3 px-4 border-b">Objektsaldon</td>
                    <td className="py-3 px-4 border-b">Kostnadsställen, projekt</td>
                  </tr>
                  <tr className="bg-green-50">
                    <td className="py-3 px-4 border-b font-medium text-green-700">SIE 4</td>
                    <td className="py-3 px-4 border-b">Alla transaktioner</td>
                    <td className="py-3 px-4 border-b font-medium text-green-700">
                      ⭐ Används för årsredovisning
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 border-b font-medium">SIE 4i</td>
                    <td className="py-3 px-4 border-b">Endast verifikationer</td>
                    <td className="py-3 px-4 border-b">Import från delsystem</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mt-6">
              <h3 className="font-semibold text-green-900 mb-2">
                Kvanten använder SIE 4 (SIE4E)
              </h3>
              <p className="text-green-800">
                För att skapa en komplett årsredovisning behöver vi tillgång till alla transaktioner
                och saldon. Därför kräver Kvanten en SIE4-fil (också kallad SIE4E när den innehåller
                verifikationer).
              </p>
            </div>
          </section>

          {/* Section 3 */}
          <section id="innehall" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Vad innehåller en SIE-fil?</h2>
            <p className="text-gray-600 mb-6">En SIE4-fil innehåller följande information:</p>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Företagsinformation</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Företagsnamn</li>
                  <li>• Organisationsnummer</li>
                  <li>• Räkenskapsår (start- och slutdatum)</li>
                  <li>• Adress (om angiven)</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Kontoplan</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Kontonummer och kontonamn</li>
                  <li>• Kontotyp (tillgång, skuld, intäkt, kostnad)</li>
                  <li>• SRU-koppling (för deklaration)</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Saldon</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Ingående balans (IB)</li>
                  <li>• Utgående balans (UB)</li>
                  <li>• Resultat per konto (RES)</li>
                  <li>• Jämförelsetal (föregående år)</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Verifikationer (SIE4E)</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Verifikationsnummer och serie</li>
                  <li>• Datum och beskrivning</li>
                  <li>• Alla transaktionsrader</li>
                  <li>• Kostnadsställen/projekt (om använda)</li>
                </ul>
              </div>
            </div>

            <div className="mt-6 p-6 bg-gray-100 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">Exempel på SIE-format</h3>
              <pre className="text-sm text-gray-700 overflow-x-auto whitespace-pre">
                {`#FLAGGA 0
#PROGRAM "Fortnox Bokföring" 4.52
#FORMAT PC8
#GEN 20240115
#SIETYP 4
#FNAMN "Exempelföretaget AB"
#ORGNR 5561234567
#RAR 0 20240101 20241231
#KONTO 1910 "Kassa"
#KONTO 3010 "Försäljning varor"
#IB 0 1910 50000.00
#UB 0 1910 75000.00
#VER A 1 20240115 "Kontantförsäljning"
{
  #TRANS 1910 {} 1000.00
  #TRANS 3010 {} -1000.00
}`}
              </pre>
            </div>
          </section>

          {/* Section 4 */}
          <section id="exportera" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Så exporterar du SIE</h2>
            <p className="text-gray-600 mb-6">
              Så här exporterar du en SIE4-fil från de vanligaste bokföringsprogrammen:
            </p>

            <div className="space-y-6">
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Fortnox</h3>
                <ol className="space-y-2 text-gray-600">
                  <li>1. Gå till Bokföring → Verifikationer</li>
                  <li>2. Klicka på "Exportera" i verktygsfältet</li>
                  <li>3. Välj "SIE4" som format</li>
                  <li>4. Välj räkenskapsår och klicka "Exportera"</li>
                </ol>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Visma eEkonomi / Visma Spcs</h3>
                <ol className="space-y-2 text-gray-600">
                  <li>1. Gå till Arkiv → Import/Export → Exportera SIE</li>
                  <li>2. Välj filtyp "SIE typ 4"</li>
                  <li>3. Markera "Inkludera verifikationer"</li>
                  <li>4. Välj period och spara filen</li>
                </ol>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Björn Lundén</h3>
                <ol className="space-y-2 text-gray-600">
                  <li>1. Gå till Arkiv → Exportera → SIE-export</li>
                  <li>2. Välj "SIE4E - komplett export"</li>
                  <li>3. Ange räkenskapsår</li>
                  <li>4. Välj sökväg och spara</li>
                </ol>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Speedledger</h3>
                <ol className="space-y-2 text-gray-600">
                  <li>1. Gå till Administration → Exportera data</li>
                  <li>2. Välj "SIE-fil"</li>
                  <li>3. Välj "Typ 4" och räkenskapsår</li>
                  <li>4. Klicka "Exportera"</li>
                </ol>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mt-6">
              <h3 className="font-semibold text-amber-900 mb-2">Kontrollera före export</h3>
              <p className="text-amber-800">
                Innan du exporterar SIE-filen, se till att:
              </p>
              <ul className="mt-2 space-y-1 text-amber-800">
                <li>• All bokföring för räkenskapsåret är registrerad</li>
                <li>• Bokslutstransaktioner (avskrivningar, periodiseringar) är bokförda</li>
                <li>• Du väljer rätt räkenskapsår</li>
                <li>• Du väljer SIE typ 4 (inte typ 1 eller 2)</li>
              </ul>
            </div>
          </section>

          {/* Section 5 */}
          <section id="vanliga-fragor" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Vanliga frågor</h2>

            <div className="space-y-6">
              <div className="border-b border-gray-200 pb-6">
                <h3 className="font-semibold text-gray-900 mb-2">
                  Varför kan jag inte öppna SIE-filen?
                </h3>
                <p className="text-gray-600">
                  SIE-filer är textfiler med svensk teckenkodning (CP437 eller ISO-8859-1). Om du
                  öppnar dem i en vanlig textredigerare kan svenska tecken (åäö) se konstiga ut.
                  Detta påverkar inte funktionen - Kvanten hanterar kodningen automatiskt.
                </p>
              </div>

              <div className="border-b border-gray-200 pb-6">
                <h3 className="font-semibold text-gray-900 mb-2">
                  Vad betyder "flagga 0" och "flagga 1"?
                </h3>
                <p className="text-gray-600">
                  Flaggan indikerar om filen är komplett. Flagga 0 betyder att exporten är komplett,
                  medan flagga 1 indikerar att filen är ofullständig (t.ex. pågående arbete). Kvanten
                  kräver flagga 0 för att säkerställa komplett data.
                </p>
              </div>

              <div className="border-b border-gray-200 pb-6">
                <h3 className="font-semibold text-gray-900 mb-2">
                  Kan jag använda en gammal SIE-fil?
                </h3>
                <p className="text-gray-600">
                  SIE-formatet har varit stabilt sedan 1990-talet, så äldre filer fungerar. Dock bör
                  du alltid exportera en ny fil före årsredovisningen för att säkerställa att all
                  data är aktuell.
                </p>
              </div>

              <div className="pb-6">
                <h3 className="font-semibold text-gray-900 mb-2">
                  Vad händer om jämförelsetal saknas?
                </h3>
                <p className="text-gray-600">
                  Om det är företagets första räkenskapsår, eller om föregående års data inte finns i
                  SIE-filen, kan du antingen ladda upp en separat SIE-fil för föregående år eller
                  ange jämförelsetal manuellt i Kvanten.
                </p>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Har du din SIE-fil redo?</h2>
            <p className="text-purple-100 mb-6">
              Ladda upp den till Kvanten och ha din årsredovisning klar på under 30 minuter. Helt
              gratis att testa.
            </p>
            <Link
              to="/registrera"
              className="inline-flex items-center px-6 py-3 text-lg font-medium text-purple-600 bg-white rounded-lg hover:bg-purple-50 transition-colors"
            >
              Kom igång gratis
            </Link>
          </section>

          {/* Related articles */}
          <section className="mt-12">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Relaterade guider</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  title: "Första årsredovisningen",
                  href: "/arsredovisning/guider/forsta-arsredovisningen",
                  description: "Komplett guide för nystartade företag",
                },
                {
                  title: "K2 vs K3",
                  href: "/arsredovisning/guider/k2-vs-k3",
                  description: "Välj rätt regelverk för ditt företag",
                },
              ].map((article, i) => (
                <Link
                  key={i}
                  to={article.href}
                  className="block p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <h3 className="font-semibold text-gray-900 mb-2">{article.title}</h3>
                  <p className="text-sm text-gray-600">{article.description}</p>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </article>
    </>
  );
}
